import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { Operator } from '@procureai/query-builder';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { CommodityGroupFilterField, SupplierRelationshipType } from '@/generated/gql/graphql';
import {
  SupplierDataGridField,
  SupplierDataGridColumnGroup,
  SupplierDataGridRow,
  I18nNamespace,
  BaseDomainType,
} from '~/common/types';
import { PrequalificationLevelSelections } from '~/common/types/PrequalificationLevel';
import { CuriosityListCountCellMenu } from '~/components/CuriosityListCountCellMenu/CuriosityListCountCellMenu';
import {
  ActiveCell,
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  ExternalLinkCell,
  gridColDefFactory,
  gridColumnGroupFactory,
  GridSortDirectionType,
  headerGroupWithIcon,
  LinkCell,
  LinkCellValue,
  ListCountCell,
  numberFormatterFactory,
  StatusCell,
  TooltipCell,
  TooltipCellValue,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { IconId } from '~/components/Icon';
import { ListCountCellMenu } from '~/components/ListCountCellMenu/ListCountCellMenu';
import { LocationLink } from '~/components/LocationLink/LocationLink';
import { SourceLinksButton } from '~/components/SourceLinksButton/SourceLinksButton';
import { RouteId } from '~/routes';
import { notEmpty } from '~/util/array';
import { locationLabel } from '~/util/string';

const relationshipColorMap: { [key in SupplierRelationshipType]?: string } = {
  [SupplierRelationshipType.known]: 'success.main',
  [SupplierRelationshipType.partner]: 'secondary.main',
};
export const statusColorFromRelationship = (value: SupplierRelationshipType): string | undefined => {
  return value ? relationshipColorMap[value] : undefined;
};

export const useSupplierDataGridConfig: UseDataGridConfig<SupplierDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Base.supplier.columns' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'Base.supplier.columnBands' });
  const { t: tCountries } = useTranslation(I18nNamespace.countries, {});
  const tLocation = useMemo(() => locationLabel(tCountries), [tCountries]);

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: SupplierDataGridField.NAME, sort: GridSortDirectionType.ASC }],
      columnVisibilityModel: {
        [SupplierDataGridField.EXTERNAL_ID]: false,
        [SupplierDataGridField.EMPLOYEE_COUNT]: false,
        [SupplierDataGridField.LEGAL_TYPE]: false,
        [SupplierDataGridField.REPRESENTATIVE_COUNT]: false,
        [SupplierDataGridField.QUOTATION_COUNT]: false,
        [SupplierDataGridField.QUOTATION_SUCCESS_RATE]: false,
        [SupplierDataGridField.ORDERED_PRODUCT_COUNT]: false,
        [SupplierDataGridField.ORDERED_PRODUCT_IN_CATEGORY_COUNT]: false,
        [SupplierDataGridField.ORDERED_SERVICE_COUNT]: false,
        [SupplierDataGridField.ORDERED_SERVICE_IN_CATEGORY_COUNT]: false,
        [SupplierDataGridField.ORDER_LAST_DATE]: false,
        [SupplierDataGridField.ORDER_VOLUME]: false,
        [SupplierDataGridField.ORDER_COUNT]: false,
        [SupplierDataGridField.REVENUE]: false,
        [SupplierDataGridField.EBIT]: false,
        [SupplierDataGridField.PE_RATIO]: false,
      },
      columns: [
        createColumn({
          field: SupplierDataGridField.NAME,
          type: undefined,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value: SupplierDataGridRow['name'], row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to:
              row.relationship === SupplierRelationshipType.unknown
                ? RouteId.companyDetail(row.id)
                : RouteId.supplierDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 350,
          hideable: false,
        }),
        createColumn({
          field: SupplierDataGridField.EXTERNAL_ID,
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.EMPLOYEE_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.INDUSTRY,
          valueGetter: (value: SupplierDataGridRow['industry']): TooltipCellValue => {
            return value?.name;
          },
          renderCell: cellFactory(TooltipCell),
          minWidth: 150,
        }),
        createColumn({
          field: SupplierDataGridField.LEGAL_TYPE,
          valueGetter: (value: SupplierDataGridRow['legalType']): Union.Nullable<string> => {
            return value?.name;
          },
          minWidth: 150,
        }),
        createColumn({
          field: SupplierDataGridField.RELATIONSHIP,
          renderCell: cellFactory(StatusCell, ({ value }) => ({
            statusColor: statusColorFromRelationship(value),
            i18nKeyPrefix: 'Base.supplier.relationship',
          })),
          minWidth: 150,
        }),
        createColumn({
          field: SupplierDataGridField.PREQUALIFICATION_LEVEL,
          renderCell: cellFactory(ActiveCell, ({ value, row }) => ({
            value: !(value === PrequalificationLevelSelections.None),
            row,
          })),
          minWidth: 200,
        }),
        createColumn({
          field: SupplierDataGridField.PREQUALIFICATION_TRADES,
          renderCell: cellFactory(ListCountCell, ({ value }) => ({
            value: value?.length,
            renderList: notEmpty(value) && (() => <ListCountCellMenu list={value} />),
          })),
          minWidth: 200,
          sortable: false,
        }),
        createColumn({
          field: SupplierDataGridField.LOCATION_GROUP_LIST,
          renderCell: cellFactory(ListCountCell, ({ value }) => ({
            value: value?.length,
            renderList: notEmpty(value) && (() => <ListCountCellMenu list={value} />),
          })),
          valueFormatter: (value: SupplierDataGridRow['locationGroupList']) =>
            value && Array.isArray(value) && value.map(({ name }) => name).join(', '),
          minWidth: 140,
          sortable: false,
        }),
        createColumn({
          field: SupplierDataGridField.LOCATION,
          valueGetter: (_value, row) => row.address,
          renderCell: cellFactory(LocationLink),
          valueFormatter: (value) => tLocation(value),
          minWidth: 600,
          sortable: false,
        }),
        createColumn({
          field: SupplierDataGridField.REPRESENTATIVE_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.representativeList) && (() => <ListCountCellMenu list={row.representativeList} />),
          })),
          minWidth: 140,
        }),
        createColumn({
          field: SupplierDataGridField.TAG_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.tagList) && (() => <ListCountCellMenu list={row.tagList} />),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: SupplierDataGridField.COMMODITY_GROUP_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.commodityGroupList) &&
              (() => (
                <CuriosityListCountCellMenu
                  ns={CuriosityNamespaceType.COMMODITY_GROUP}
                  list={row.commodityGroupList}
                  link={{
                    needs: { permission: Permission.BaseCommodityGroupList },
                    all: {
                      label: t('Base.order.menu.commodityGroupCount.all'),
                      pathname: RouteId.analyticsBase(BaseDomainType.COMMODITY_GROUP),
                      rules: [
                        {
                          field: CommodityGroupFilterField.id,
                          operator: Operator.INEQ,
                          value: row.commodityGroupList.map(({ id }) => id),
                        },
                      ],
                    },
                    to: (id) => RouteId.commodityGroupDetail(id),
                    component: Link,
                  }}
                />
              )),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: SupplierDataGridField.ACTIVE_CONTRACT_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.activeContractList) && (() => <ListCountCellMenu list={row.activeContractList} />),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: SupplierDataGridField.QUOTATION_SUCCESS_RATE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 180,
        }),
        createColumn({
          field: SupplierDataGridField.ORDERED_PRODUCT_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.orderedProductList) && (() => <ListCountCellMenu list={row.orderedProductList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: SupplierDataGridField.ORDERED_PRODUCT_IN_CATEGORY_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.orderedProductInCategoryList) &&
              (() => <ListCountCellMenu list={row.orderedProductInCategoryList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: SupplierDataGridField.ORDERED_SERVICE_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.orderedServiceList) && (() => <ListCountCellMenu list={row.orderedServiceList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: SupplierDataGridField.ORDERED_SERVICE_IN_CATEGORY_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.orderedServiceInCategoryList) &&
              (() => <ListCountCellMenu list={row.orderedServiceInCategoryList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_LAST_DATE,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_VOLUME_YEAR,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_COUNT_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 200,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_VOLUME,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_VOLUME_SHARE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_VOLUME_SHARE_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_VOLUME_CUMULATIVE,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.ORDER_VOLUME_CUMULATIVE_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),

        createColumn({
          field: SupplierDataGridField.ORDER_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.REVENUE,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
        }),
        createColumn({
          field: SupplierDataGridField.EBIT,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
        }),
        createColumn({
          field: SupplierDataGridField.PE_RATIO,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: SupplierDataGridField.SUPPLIER_PHONE,
          valueGetter: (_value, row) => row.contact.phone,
          minWidth: 120,
          sortable: false,
        }),
        createColumn({
          field: SupplierDataGridField.SUPPLIER_FAX,
          valueGetter: (_value, row) => row.contact.fax,
          minWidth: 120,
          sortable: false,
        }),
        createColumn({
          field: SupplierDataGridField.SUPPLIER_EMAIL,
          valueGetter: (_value, row) => row.contact.email,
          minWidth: 120,
          sortable: false,
        }),
        createColumn({
          field: SupplierDataGridField.SUPPLIER_WEBSITE,
          valueGetter: (_value, row) => row.contact.website,
          renderCell: cellFactory(ExternalLinkCell),
          minWidth: 120,
          sortable: false,
        }),

        createColumn({
          field: SupplierDataGridField.LINKS,
          renderCell: cellFactory(SourceLinksButton, ({ value }) => ({ links: value })),
          minWidth: 120,
          sortable: false,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: SupplierDataGridColumnGroup.SUPPLIER,
          renderHeaderGroup: headerGroupWithIcon(IconId.supplier),
          children: [
            //
            { field: SupplierDataGridField.NAME },
            { field: SupplierDataGridField.EXTERNAL_ID },
            { field: SupplierDataGridField.EMPLOYEE_COUNT },
            { field: SupplierDataGridField.INDUSTRY },
            { field: SupplierDataGridField.LEGAL_TYPE },
            { field: SupplierDataGridField.RELATIONSHIP },
            { field: SupplierDataGridField.PREQUALIFICATION_LEVEL },
            { field: SupplierDataGridField.PREQUALIFICATION_TRADES },
            { field: SupplierDataGridField.LOCATION_GROUP_LIST },
            { field: SupplierDataGridField.LOCATION },
            { field: SupplierDataGridField.REPRESENTATIVE_COUNT },
            { field: SupplierDataGridField.TAG_COUNT },
            { field: SupplierDataGridField.COMMODITY_GROUP_COUNT },
          ],
        }),
        createGroup({
          groupId: SupplierDataGridColumnGroup.CONTRACT,
          renderHeaderGroup: headerGroupWithIcon(IconId.contract),
          children: [
            //
            { field: SupplierDataGridField.ACTIVE_CONTRACT_COUNT },
            { field: SupplierDataGridField.QUOTATION_COUNT },
            { field: SupplierDataGridField.QUOTATION_SUCCESS_RATE },
          ],
        }),
        createGroup({
          groupId: SupplierDataGridColumnGroup.PRODUCT,
          renderHeaderGroup: headerGroupWithIcon(IconId.Widgets),
          children: [
            //
            { field: SupplierDataGridField.ORDERED_PRODUCT_COUNT },
            { field: SupplierDataGridField.ORDERED_PRODUCT_IN_CATEGORY_COUNT },
          ],
        }),
        createGroup({
          groupId: SupplierDataGridColumnGroup.SERVICE,
          renderHeaderGroup: headerGroupWithIcon(IconId.Person),
          children: [
            //
            { field: SupplierDataGridField.ORDERED_SERVICE_COUNT },
            { field: SupplierDataGridField.ORDERED_SERVICE_IN_CATEGORY_COUNT },
          ],
        }),
        createGroup({
          groupId: SupplierDataGridColumnGroup.ORDER,
          renderHeaderGroup: headerGroupWithIcon(IconId.order),
          children: [
            //
            { field: SupplierDataGridField.ORDER_LAST_DATE },
            { field: SupplierDataGridField.ORDER_VOLUME_YEAR },
            { field: SupplierDataGridField.ORDER_COUNT_YEAR },
            { field: SupplierDataGridField.ORDER_VOLUME },
            { field: SupplierDataGridField.ORDER_COUNT },
            { field: SupplierDataGridField.ORDER_VOLUME_SHARE },
            { field: SupplierDataGridField.ORDER_VOLUME_SHARE_YEAR },
            { field: SupplierDataGridField.ORDER_VOLUME_CUMULATIVE },
            { field: SupplierDataGridField.ORDER_VOLUME_CUMULATIVE_YEAR },
          ],
        }),
        createGroup({
          groupId: SupplierDataGridColumnGroup.FINANCIAL,
          renderHeaderGroup: headerGroupWithIcon(IconId.AccountBalance),
          children: [
            //
            { field: SupplierDataGridField.REVENUE },
            { field: SupplierDataGridField.EBIT },
            { field: SupplierDataGridField.PE_RATIO },
          ],
        }),
        createGroup({
          groupId: SupplierDataGridColumnGroup.CONTACT,
          children: [
            //
            { field: SupplierDataGridField.SUPPLIER_PHONE },
            { field: SupplierDataGridField.SUPPLIER_FAX },
            { field: SupplierDataGridField.SUPPLIER_EMAIL },
            { field: SupplierDataGridField.SUPPLIER_WEBSITE },
          ],
        }),
      ],
      exportModel: {
        [SupplierDataGridField.QUOTATION_SUCCESS_RATE]: {
          numFmt: '0.00%',
        },
        [SupplierDataGridField.PE_RATIO]: {
          numFmt: '0.00%',
        },
        [SupplierDataGridField.LINKS]: {
          disable: true,
        },
      },
    } as DataGridConfig<SupplierDataGridRow>;
  }, [t, tColumn, tLocation, tGroup]);
};
